import React, { useEffect, useMemo, useState } from "react";
import Input, { FIELD_CLASS } from "../../Components/Input";
import Price from "../../Components/Price";
import Chip from "../../Components/Chip";
import Stepper from "../../Components/Stepper";
import Button from "../../Components/Button";
import TableBadge from "../../Components/TableBadge";
import { BackIcon, ForwardIcon } from "../../Components/Icons";
import useTableBill from "../../hooks/useTableBill";
import { getPrice } from "../../util/helper";
import { useAppContext } from "../../context/AppContext";
import { opmStorage, Keys, tableScopeOf } from "../../util/opmStorage";
import { createWebPayment } from "../../services/ApiCalls";
import { savePendingWebPayment } from "../../util/webPayment";

const PRESET_TIPS = ["10", "12", "15"];
const CUSTOM_TIP = "custom";
const NO_TIP = "0";

const round2 = (n) => Math.round(n * 100) / 100;

const Section = ({ title, children, aside }) => (
  <section className="flex flex-col gap-2">
    <div className="flex items-center justify-between">
      <h2 className="text-sm font-bold text-dark">{title}</h2>
      {aside}
    </div>
    {children}
  </section>
);

/**
 * The table's bill comes from the server once the guest entered the table code: every
 * diner's dishes, what was already paid and what remains. It is the only source: dishes in
 * this phone's cart were not sent to the kitchen and cannot be paid for, so without the bill
 * (no table code yet, or the request failed) the screen says why instead of showing amounts.
 */
const useBillView = () => {
  const { bill, loading, failed, signedIn, reload } = useTableBill();
  return useMemo(() => {
    if (bill) {
      const items = (bill.items || []).filter((item) => !item.is_canceled);
      const paidIds = new Set((bill.paid || []).flatMap((p) => p.paid_items || []));
      const total = Number(bill.total_amount) || 0;
      const remaining =
        bill.remaining_amount != null
          ? Number(bill.remaining_amount) || 0
          : Math.max(0, total - (bill.paid || []).reduce((s, p) => s + (Number(p.amount) || 0), 0));
      const unpaid = items
        .filter((item) => !paidIds.has(item.id))
        .map((item) => ({ id: item.id, person: item.person_name || "", price: Number(item.total_price) || 0 }));
      return {
        loading,
        fromServer: true,
        total,
        remaining,
        paid: Math.max(0, total - remaining),
        unpaid,
        diners: Number(bill.number_of_people) || 0,
      };
    }
    return {
      loading,
      fromServer: false,
      failed,
      signedIn,
      reload,
      total: 0,
      remaining: 0,
      paid: 0,
      unpaid: [],
      diners: 0,
    };
  }, [bill, loading, failed, signedIn, reload]);
};

const Index = ({
  closeModal,
  t,
  wishAmount,
  setWishAmount,
  splitOptions,
  setSplitOptions,
  selectedTip,
  setSelectedTip,
  enterTip,
  setEnterTip,
  setOrderList,
  selectedPerson,
  setSelectedPerson,
  eachPerson,
  setEachPerson,
  navigate,
}) => {
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState("");
  // With the keyboard open iOS shrinks only the visual viewport, so 100dvh stays tall and the
  // footer ends up behind the keys: size the screen to what is actually visible.
  const [visibleHeight, setVisibleHeight] = useState(null);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const onResize = () => {
      setVisibleHeight(vv.height);
      setKeyboardOpen(window.innerHeight - vv.height > 120);
      window.scrollTo(0, 0);
      // The field being typed in must stay in view above the pinned button.
      const active = document.activeElement;
      if (active && active.tagName === "INPUT") {
        setTimeout(() => active.scrollIntoView({ block: "center" }), 50);
      }
    };
    vv.addEventListener("resize", onResize);
    return () => vv.removeEventListener("resize", onResize);
  }, []);
  const view = useBillView();
  const { authToken, authClaims, diningSessionId, tableLabel } = useAppContext();

  // Start the diner count from the number the waiter set on the table.
  useEffect(() => {
    if (view.diners > 1 && Number(eachPerson) <= 1) setEachPerson(view.diners);
  }, [view.diners]); // eslint-disable-line react-hooks/exhaustive-deps

  const byPerson = t("Split by person");
  const byAmount = t("Split by amount");
  const equally = t("Split equally");

  const people = [...new Set(view.unpaid.map((i) => i.person).filter(Boolean))];
  const personShare = view.unpaid
    .filter((i) => i.person === selectedPerson)
    .reduce((sum, i) => sum + i.price, 0);
  const diners = Math.max(1, Number(eachPerson) || 1);

  let base = view.remaining;
  if (splitOptions === byPerson && selectedPerson) base = personShare;
  if (splitOptions === byAmount) base = Number(wishAmount) > 0 ? Number(wishAmount) : 0;
  if (splitOptions === equally) base = view.remaining / diners;
  base = Math.min(Math.max(0, round2(base)), view.remaining);

  const tipPercent =
    selectedTip === CUSTOM_TIP
      ? Math.min(100, Math.max(0, Number(enterTip) || 0))
      : Number(selectedTip) || 0;
  const tip = round2((base * tipPercent) / 100);
  const charge = round2(base + tip);

  // The guest's share goes to the acquirer's payment page, where every method is offered and
  // the guest picks how to pay. The page only authorizes; the server captures once the bill
  // is checked.
  const pay = async () => {
    if (charge <= 0 || paying) return;
    const restaurantId = authClaims?.restaurant_id != null ? String(authClaims.restaurant_id) : null;
    const elementId = tableScopeOf(authClaims);
    const sessionId = Number(authClaims?.dining_session_id ?? diningSessionId);
    const token = authToken || (restaurantId && elementId ? opmStorage.getWithTtl(Keys.token(restaurantId, elementId)) : null);
    if (!restaurantId || !token || !Number.isInteger(sessionId)) {
      setPayError(t("payNeedsTable"));
      return;
    }
    const paidItems =
      splitOptions === byPerson && selectedPerson
        ? view.unpaid.filter((i) => i.person === selectedPerson && i.id != null).map((i) => i.id)
        : [];
    setPayError("");
    setPaying(true);
    try {
      const page = await createWebPayment(
        restaurantId,
        sessionId,
        {
          amount: base,
          tipAmount: tip,
          paidItems,
          payByUserName: splitOptions === byPerson ? selectedPerson : "",
        },
        token
      );
      savePendingWebPayment(restaurantId, elementId, {
        uniqueId: page.unique_id,
        sessionId: page.session_id,
        token: page.token,
        signature: page.signature,
        restaurantId,
        diningSessionId: sessionId,
        base,
        tip,
        charge,
      });
      window.location.assign(page.session_url);
    } catch (err) {
      setPaying(false);
      setPayError(err?.response?.status === 400 ? t("payRefused") : t("payUnavailable"));
    }
  };

  const header = (
    <header className="flex items-center gap-1 px-3 pt-2">
      <button
        type="button"
        onClick={closeModal}
        aria-label={t("Back to order")}
        className="w-11 h-11 flex items-center justify-center text-dark"
      >
        <BackIcon />
      </button>
      <h1 className="flex-1 text-lg font-bold text-dark">{t("Payment")}</h1>
      <TableBadge />
    </header>
  );

  // Without the table's bill there is nothing to pay yet: say why.
  if (!view.fromServer) {
    return (
      <div className="h-dvh flex flex-col bg-background">
        {header}
        <main className="flex-1 px-6 flex flex-col items-center justify-center gap-4 text-center">
          {view.signedIn && !view.failed ? (
            <span
              className="w-10 h-10 rounded-full border-4 border-primary-50 border-t-primary animate-spin"
              role="img"
              aria-label={t("loading")}
            />
          ) : (
            <>
              <p className="text-base text-dark max-w-xs">
                {view.signedIn ? t("billLoadFailed") : t("payNeedsTable")}
              </p>
              {view.signedIn ? (
                <Button size="lg" onClick={view.reload}>
                  {t("tryAgain")}
                </Button>
              ) : (
                <Button size="lg" onClick={() => navigate("/")}>
                  {t("Back to Menu")}
                </Button>
              )}
            </>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="h-dvh flex flex-col bg-background" style={visibleHeight ? { height: visibleHeight } : undefined}>
      {header}

      <main className="flex-1 overflow-y-auto scrollbar-hide px-5 pt-3 pb-6 flex flex-col gap-5">
        <section className="rounded-xl bg-primary text-white px-5 py-[18px] flex flex-col gap-2.5 shadow-brand">
          <span className="text-sm font-medium">
            {t("Remaining")}{tableLabel && ` · ${t("Table")} ${tableLabel}`}
          </span>
          <Price value={view.remaining} className="text-2xl font-bold" />
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            <span>
              {t("Total Amount")} <Price value={view.total} className="font-bold" />
            </span>
            <span>
              {t("Amount Paid")} <Price value={view.paid} className="font-bold" />
            </span>
          </div>
        </section>

        <Section title={t("Split Options")}>
          <div className="grid grid-cols-3 gap-2">
            {[byPerson, byAmount, equally].map((option) => (
              <Chip
                key={option}
                selected={splitOptions === option}
                onClick={() => {
                  setSplitOptions(option);
                  setWishAmount("");
                }}
                className="px-1.5"
              >
                {option}
              </Chip>
            ))}
          </div>
        </Section>

        {splitOptions === byPerson && (
          <Section
            title={t("Pay by")}
            aside={selectedPerson ? <Price value={personShare} className="text-sm font-bold text-dark" /> : null}
          >
            {people.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {people.map((person) => (
                  <Chip
                    key={person}
                    variant="outline"
                    selected={selectedPerson === person}
                    onClick={() => setSelectedPerson(person)}
                  >
                    {person}
                  </Chip>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted">{t("noNamesOnOrder")}</p>
            )}
          </Section>
        )}

        {splitOptions === byAmount && (
          <Section title={t("Enter the amount you wish to pay")}>
            <Input
              name="Amount"
              type="number"
              dir="ltr"
              className={`${FIELD_CLASS} text-center text-lg font-bold tabular-nums`}
              value={wishAmount}
              inputMode="decimal"
              onFocus={(e) => e.target.scrollIntoView({ block: "center" })}
              placeholder={getPrice(view.remaining)}
              onChange={(e) => setWishAmount(e.target.value)}
            />
          </Section>
        )}

        {splitOptions === equally && (
          <section className="flex items-center justify-between">
            <div className="flex flex-col gap-0.5">
              <h2 className="text-sm font-bold text-dark">{t("Number of people")}</h2>
              <span className="text-sm text-muted">
                <Price value={view.remaining / diners} className="text-dark font-bold" /> {t("each")}
              </span>
            </div>
            <Stepper
              value={diners}
              onDecrease={() => setEachPerson(Math.max(1, diners - 1))}
              onIncrease={() => setEachPerson(diners + 1)}
            />
          </section>
        )}

        <Section title={t("Tip for the staff")}>
          <div className="grid grid-cols-5 gap-2">
            {[...PRESET_TIPS, CUSTOM_TIP, NO_TIP].map((choice) => (
              <Chip
                key={choice}
                selected={String(selectedTip) === choice}
                onClick={() => {
                  setSelectedTip(choice);
                  if (choice !== CUSTOM_TIP) setEnterTip("");
                }}
                className="px-1 tabular-nums"
              >
                {choice === CUSTOM_TIP ? t("Other") : choice === NO_TIP ? t("No tip") : `${choice}%`}
              </Chip>
            ))}
          </div>
          {selectedTip === CUSTOM_TIP && (
            <label className="flex items-center gap-3 animate-fade-in">
              <span className="text-sm text-muted">{t("Tip percent")}</span>
              <span dir="ltr" className="flex items-center gap-1.5">
                <Input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  max="100"
                  autoFocus
                  value={enterTip}
                  onChange={(e) => setEnterTip(e.target.value)}
                  placeholder="0"
                  aria-label={t("Tip percent")}
                  className="w-20 h-11 border-[1.5px] border-secondary-dark rounded-md text-center text-base font-bold tabular-nums focus:outline-none focus:border-primary"
                />
                <span className="text-base font-bold">%</span>
              </span>
            </label>
          )}
        </Section>

        <section className="flex flex-col gap-1.5 rounded-lg bg-secondary px-4 py-3 text-sm">
          <div className="flex justify-between">
            <span className="text-muted">{t("Your share")}</span>
            <Price value={base} className="text-dark" />
          </div>
          <div className="flex justify-between">
            <span className="text-muted">
              {t("Tip")} {tipPercent > 0 ? `(${tipPercent}%)` : ""}
            </span>
            <Price value={tip} className="text-dark" />
          </div>
          <div className="flex justify-between pt-1.5 border-t border-secondary-dark font-bold text-dark">
            <span>{t("Charged now")}</span>
            <Price value={charge} />
          </div>
        </section>
      </main>

      <div
        className="flex-none bg-background px-6 pt-3 flex flex-col gap-2.5 shadow-sheet"
        style={{ paddingBottom: keyboardOpen ? "12px" : "calc(24px + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="flex items-baseline justify-between text-sm">
          <span className="text-muted">{t("Pay now")}</span>
          <Price value={charge} className="text-lg font-bold text-primary" />
        </div>
        {payError && (
          <p role="alert" className="text-sm text-danger text-center">
            {payError}
          </p>
        )}
        <Button size="lg" fullWidth onClick={pay} disabled={paying || charge <= 0}>
          {paying ? (
            t("Paying")
          ) : (
            <>
              {t("Continue to payment")}
              <ForwardIcon size={20} />
            </>
          )}
        </Button>
        {!keyboardOpen && (
          <p className="flex items-center justify-center gap-2 text-xs text-muted" aria-label={t("paymentMethodsAccepted")}>
            <span dir="ltr">Apple Pay</span>
            <span aria-hidden="true">·</span>
            <span dir="ltr">Google Pay</span>
            <span aria-hidden="true">·</span>
            <span>{t("card")}</span>
          </p>
        )}
      </div>
    </div>
  );
};

export default Index;
